import { Capacitor } from '@capacitor/core';

import { Logger } from '@mopla/logger';

import { environment } from '../../environments/environment';

import { loadCookieScript } from './firebase/cookieScript';
import { bridgeDataLayerToFirebase } from './firebase/firebaseBridge';
import { applyConsentToFirebase } from './firebase/firebaseConsent';
import { initGoogleTagManager } from './events';

/**
 * Sets up the analytics event bus and the platform's delivery path.
 *
 * The dataLayer is created first because both paths read from it, and the
 * Firebase bridge binds to the array directly.
 *
 * - Web loads the GTM container, which delivers to GA4 and also injects the
 *   CookieScript banner.
 * - Native apps do not load GTM at all: its transport is blocked by the
 *   "capacitor://" origin on iOS, and the native SDK additionally provides app
 *   lifecycle events, offline queueing and app version dimensions. Events are
 *   routed to Firebase Analytics, and the consent banner is injected directly
 *   because it would otherwise arrive with the container. Its decision is
 *   handed to the SDK via Consent Mode (see ./firebase/firebaseConsent). The
 *   bridge only starts once the stored decision is applied, so early events
 *   are not sent with the denied defaults by mistake (see
 *   ./firebase/cookieScript).
 */
export function initUserTracking() {
	const logger = Logger.getInstance('UserTracking');
	const isNative = Capacitor.isNativePlatform();

	logger.debug('User tracking: init', {
		platform: Capacitor.getPlatform(),
		isNative,
		env: environment.env,
		origin: window.location.origin,
	});

	//ensure datalayer
	window.dataLayer = window.dataLayer || [];

	if (isNative) {
		logger.debug('User tracking: native path, routing to Firebase Analytics');
		// The bridge starts once the stored consent is applied. Events pushed
		// until then wait in the dataLayer and are drained by the bridge.
		loadCookieScript(applyConsentToFirebase, bridgeDataLayerToFirebase);
		return;
	}

	logger.debug('User tracking: web path, initialising GTM');
	initGoogleTagManager();
}
